import type {
  GetSpacesRequest,
  GetSpacesResponse,
} from "@common/api-interface/spaces";
import type { TrpcClient } from "@common/api-interface/trpc-client";
import type { Space, SpaceId } from "@common/types/space";
import type { SpaceUserRole } from "@common/types/space-access";
import type { UserId } from "@common/types/user";
import type { Http } from "@common/utils/http";

export type SpaceService = ReturnType<typeof makeSpaceService>;

export const makeSpaceService = ({
  Http,
  trpcClient,
}: {
  Http: Http;
  trpcClient: TrpcClient;
}) => {
  let spaceService = {
    getSpace: (spaceId: string) =>
      trpcClient.spaces.getSpace.query({ spaceId }),
    getSpaces,
    getSpaceUsersRoles: (spaceId: string) =>
      trpcClient.spaceAccess.getSpaceUserRoles.query({ spaceId }),
    updateUserRole: (userId: UserId, role: SpaceUserRole, spaceId: SpaceId) =>
      trpcClient.spaceAccess.updateUserRole.mutate({ userId, role, spaceId }),
    createSpace: (space: Omit<Space, "id" | "createdBy" | "createdDate">) =>
      trpcClient.spaces.createSpace.mutate(space),
    updateSpace: (spaceId: string, space: Pick<Space, "name" | "icon">) =>
      trpcClient.spaces.updateSpace.mutate({
        space: { id: spaceId, ...space },
      }),
    deleteSpace: (spaceId: string) =>
      trpcClient.spaces.deleteSpace.mutate({ spaceId }),
    getShareLinkForSpace: (spaceId: string) =>
      trpcClient.spaceAccess.getShareLinkForSpace.query({ spaceId }),
    getJoinShareLinkDetails: (linkId: string) =>
      trpcClient.spaceAccess.getJoinShareLinkDetails.query({ linkId }),
    joinSpaceFromShareLink: (linkId: string) =>
      trpcClient.spaceAccess.joinSpaceFromShareLink.mutate({ linkId }),
  };

  return spaceService;

  async function getSpaces(forUser: UserId) {
    let res = await Http.json<GetSpacesRequest, GetSpacesResponse>(
      "GET",
      `/v1/users/${forUser}/spaces`
    );

    if (res) {
      return res.spaces;
    }

    throw new Error("Malformed response");
  }
};
